import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';

// WORLD-LEVEL master catalogs (Phase 2, decisions log #5).
//
//   /master-catalog/:kind            super admin curation + open reads
//   /catalog/:kind/pick              tenant copies master items into its list
//   /catalog/:kind/proposals         tenant proposes a private item upward
//   /catalog/proposals               guard-scoped listing (tenant: own;
//                                    super admin: every hospital's)
//   /master-catalog/proposals/:id/decide   super admin approve / reject
//
// State is PER KIND (byKind[kind]) so the medicine page and the lab page can
// be open in two tabs without clobbering each other's list.

const reject = (error, fallback) => ({
  error: error.response?.data?.message || error.message || fallback,
  code: error.response?.data?.code,
  masterId: error.response?.data?.masterId,
});

export const fetchCatalog = createAsyncThunk(
  'masterCatalog/fetchCatalog',
  async ({ kind, page = 1, limit = 12, search = '', status = '' }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      const response = await axiosInstance.get(`${baseURL}/master-catalog/${kind}?${params}`);
      return { kind, ...response.data };
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to load the catalog'));
    }
  }
);

export const createCatalogItem = createAsyncThunk(
  'masterCatalog/createCatalogItem',
  async ({ kind, data }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const response = await axiosInstance.post(`${baseURL}/master-catalog/${kind}`, data);
      return { kind, ...response.data };
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to create the item'));
    }
  }
);

export const updateCatalogItem = createAsyncThunk(
  'masterCatalog/updateCatalogItem',
  async ({ kind, id, data }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const response = await axiosInstance.put(`${baseURL}/master-catalog/${kind}/${id}`, data);
      return { kind, ...response.data };
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to update the item'));
    }
  }
);

export const toggleCatalogItem = createAsyncThunk(
  'masterCatalog/toggleCatalogItem',
  async ({ kind, id }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const response = await axiosInstance.post(`${baseURL}/master-catalog/${kind}/${id}/toggle-status`);
      return { kind, ...response.data };
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to toggle status'));
    }
  }
);

export const deleteCatalogItem = createAsyncThunk(
  'masterCatalog/deleteCatalogItem',
  async ({ kind, id }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const response = await axiosInstance.delete(`${baseURL}/master-catalog/${kind}/${id}`);
      return { kind, id, ...response.data };
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to delete the item'));
    }
  }
);

// ---- tenant side -----------------------------------------------------------

export const pickFromMaster = createAsyncThunk(
  'masterCatalog/pickFromMaster',
  async ({ kind, ids }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const response = await axiosInstance.post(`${baseURL}/catalog/${kind}/pick`, { ids });
      return { kind, ...response.data };
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to pick items'));
    }
  }
);

export const proposeToMaster = createAsyncThunk(
  'masterCatalog/proposeToMaster',
  async ({ kind, data }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const response = await axiosInstance.post(`${baseURL}/catalog/${kind}/proposals`, data);
      return { kind, ...response.data };
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to send the proposal'));
    }
  }
);

export const fetchProposals = createAsyncThunk(
  'masterCatalog/fetchProposals',
  async ({ kind = '', status = '', page = 1, limit = 50 } = {}, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const params = new URLSearchParams({ page, limit });
      if (kind) params.set('kind', kind);
      if (status) params.set('status', status);
      const response = await axiosInstance.get(`${baseURL}/catalog/proposals?${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to load proposals'));
    }
  }
);

export const decideProposal = createAsyncThunk(
  'masterCatalog/decideProposal',
  async ({ proposalId, approve, reviewNote }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const response = await axiosInstance.post(
        `${baseURL}/master-catalog/proposals/${proposalId}/decide`,
        { approve, reviewNote }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(reject(error, 'Failed to decide the proposal'));
    }
  }
);

const emptyKindState = { items: [], count: 0, page: 1, limit: 12, loading: false };

const masterCatalogSlice = createSlice({
  name: 'masterCatalog',
  initialState: {
    byKind: {},          // kind -> { items, count, page, limit, loading }
    actionLoading: false, // create / update / toggle / delete
    pickLoading: false,
    proposeLoading: false,
    proposals: { list: [], count: 0, page: 1, limit: 50, loading: false },
    decideLoading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCatalog.pending, (state, action) => {
        const kind = action.meta.arg.kind;
        state.byKind[kind] = { ...(state.byKind[kind] || emptyKindState), loading: true };
        state.error = null;
      })
      .addCase(fetchCatalog.fulfilled, (state, action) => {
        const { kind, items, count, page, limit } = action.payload;
        state.byKind[kind] = { items, count, page, limit, loading: false };
      })
      .addCase(fetchCatalog.rejected, (state, action) => {
        const kind = action.meta.arg.kind;
        state.byKind[kind] = { ...(state.byKind[kind] || emptyKindState), loading: false };
        state.error = action.payload;
      })
      .addCase(fetchProposals.pending, (state) => { state.proposals.loading = true; })
      .addCase(fetchProposals.fulfilled, (state, action) => {
        const { proposals, count, page, limit } = action.payload;
        state.proposals = { list: proposals, count, page: page || 1, limit: limit || 50, loading: false };
      })
      .addCase(fetchProposals.rejected, (state, action) => {
        state.proposals.loading = false;
        state.error = action.payload;
      })
      .addCase(pickFromMaster.pending, (state) => { state.pickLoading = true; })
      .addCase(pickFromMaster.fulfilled, (state) => { state.pickLoading = false; })
      .addCase(pickFromMaster.rejected, (state) => { state.pickLoading = false; })
      .addCase(proposeToMaster.pending, (state) => { state.proposeLoading = true; })
      .addCase(proposeToMaster.fulfilled, (state) => { state.proposeLoading = false; })
      .addCase(proposeToMaster.rejected, (state) => { state.proposeLoading = false; })
      .addCase(decideProposal.pending, (state) => { state.decideLoading = true; })
      .addCase(decideProposal.fulfilled, (state, action) => {
        state.decideLoading = false;
        // Swap the decided proposal in place so the queue updates without a refetch.
        const updated = action.payload.proposal;
        if (updated) {
          const i = state.proposals.list.findIndex((p) => p.proposalId === updated.proposalId);
          if (i !== -1) state.proposals.list[i] = updated;
        }
      })
      .addCase(decideProposal.rejected, (state) => { state.decideLoading = false; });

    // One matcher for the four curation writes — they share a spinner.
    for (const thunk of [createCatalogItem, updateCatalogItem, toggleCatalogItem, deleteCatalogItem]) {
      builder
        .addCase(thunk.pending, (state) => { state.actionLoading = true; })
        .addCase(thunk.fulfilled, (state) => { state.actionLoading = false; })
        .addCase(thunk.rejected, (state) => { state.actionLoading = false; });
    }
  },
});

export default masterCatalogSlice.reducer;
